import getConfig from 'next/config'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { FiChevronRight } from 'react-icons/fi'
import slugify from 'slugify'
import { UnitApi } from 'src/common/types'
import { getListings, getNewVerifiedListings } from 'src/data/newListings'
import { segment } from 'src/lib/Segments'

import OICircularLoader from '@components/UI/atoms/OICircularLoader'
import BlogCard from '@components/cards/BlogCard'
import ResponsiveCardGrid from '@components/generic/ResponsiveCardGrid'
import Layout from '@components/layouts'
import RentCalculator from '@components/newLanding/RentCalculator'
import SpecialGems from '@components/newLanding/SpecialGems'
import DownloadApp from '@components/newLanding/downloadApp'
import LandingFaqs from '@components/newLanding/faqs'
import MarketPlace from '@components/newLanding/marketPlace'
// import SocialLinks from '@components/newLanding/socialLinks'
import ReadReview from '@components/newLayout/readReview'
import RentApartment from '@components/newLayout/rentApartment'
import ListingUnitCards from '@components/newUnit/listingUnitCards'
// import { Resource } from '@openigloo/common'
import {
  fetchFeaturedResources,
  fetchTopRatedBuildings,
  fetchTopRatedContacts,
  fetchTopReviewedBuildings,
  fetchTopReviewedContacts,
  setCurrentCity,
  setIsLoggedIn,
} from '@redux/actions'
import { useAppSelector, useThunkDispatch } from '@redux/hooks'
import { wrapper } from '@redux/store'

const ReviewsTab = dynamic(() => import('@components/newLanding/ReviewsTab'), {
  ssr: false,
  loading: () => (
    <div className="flex h-80 w-full items-center justify-center">
      <OICircularLoader size={20} strokeWidth={2} />
    </div>
  ),
})

const TabSelector = dynamic(() => import('@components/newLanding/TabSelector'), {
  ssr: false,
  loading: () => (
    <div className="mx-auto flex w-full max-w-sm flex-row items-center justify-center rounded-3xl bg-light-20">
      <div className="w-1/2 cursor-pointer rounded-3xl py-3 text-center text-sm text-mid-300">
        Rent Apartments
      </div>
      <div className="w-1/2 cursor-pointer rounded-3xl py-3 text-center text-sm text-mid-300">
        Read Reviews
      </div>
    </div>
  ),
})

type Props = {
  title?: string
  url?: string | null
  initialList: UnitApi[]
}

const {
  publicRuntimeConfig: { STATIC_ROOT },
} = getConfig()

const openiglooLogo = `${STATIC_ROOT}/logo.png`

const NewLandingPage = ({
  title = 'Read Landlord Reviews & Find Highly-Rated Apartments',
  url = 'https://www.openigloo.com/',
  initialList,
}: Props) => {
  const { isLoggedIn, token } = useAppSelector((state) => state.currentUser)
  const { cities, featuredResources } = useAppSelector((state) => state.misc)
  const { homepageSelectedTab: selectedTab } = useAppSelector((state) => state.userActivity)
  const dispatch = useThunkDispatch()
  const router = useRouter()
  const cover = '/landing-cover.png'

  const [listingsDataCount, setListingsDataCount] = useState(null)
  const [listingsLoad, setListingsLoad] = useState(true)
  const { filters, searchFilter, profileFilter } = useAppSelector((state) => state.newSearch)

  const sortedFeaturedResources = [...featuredResources]?.sort((a, b) => b.order - a.order)

  useEffect(() => {
    if (router.isReady && cities?.length > 0) {
      let city = cities.find(
        (x) => slugify(x.name.toLowerCase()) === (router?.query?.city ?? 'new-york-city')
      )
      if (city) {
        dispatch(setCurrentCity(city))
        localStorage.setItem('city', JSON.stringify(city))
      } else if (router?.query?.city === 'anywhere') {
        city = {
          id: 'other',
          name: 'Anywhere',
          center: {
            latitude: 40.7128,
            longitude: -74.006,
          },
        }
        dispatch(setCurrentCity(city))
        localStorage.setItem('city', JSON.stringify(city))
      }
    }
  }, [router, cities])

  useEffect(() => {
    if (!isLoggedIn) {
      dispatch(setIsLoggedIn(false))
    }
  }, [])

  async function fetchListings(pageNo: number) {
    setListingsLoad(true)
    try {
      const data = {
        filters: { ...filters, neighborhoodIds: searchFilter.neighborhoodIds, ...profileFilter },
        buildingSlugs: searchFilter.buildingId ? [searchFilter.buildingId] : [],
      }

      const response = await getListings(token ?? null, data, pageNo ?? pageNo, 1)
      setListingsDataCount(response.count)
    } catch (error) {
      console.error('Failed to fetch listings:', error)
    } finally {
      setListingsLoad(false)
    }
  }

  useEffect(() => {
    if (filters && searchFilter && profileFilter) {
      fetchListings(1)
    }
  }, [filters, searchFilter, profileFilter])

  return (
    <Layout title={title} url={url} cover={cover} withBrand={false} canonicalURL={url}>
      <div>
        <div className="relative z-20 bg-light-10">
          <div className="mx-auto max-w-screen-lg px-4 pt-4 pb-4 sm:pb-0 sm:pt-6 lg:px-0">
            <div className="mx-auto flex w-fit items-center space-x-6 md:relative md:z-[52] md:-mt-20 md:block md:space-x-0">
              <div className="block md:hidden">
                <Link href="/" passHref onClick={() => segment?.header_logo()}>
                  <a
                    onClick={segment.header_logo}
                    className="relative flex h-14 w-24 items-center justify-between hover:cursor-pointer"
                  >
                    <Image
                      src={openiglooLogo}
                      alt="Home"
                      layout="fill"
                      objectFit="contain"
                      unoptimized
                    />
                  </a>
                </Link>
              </div>
              <h1 className="block text-left text-xl font-semibold text-dark-900 sm:text-center md:hidden">
                Your entire New York City rental journey in one place
              </h1>
            </div>
            <h1 className="mt-20 mb-6 hidden text-center text-xl font-semibold text-dark-900 md:block">
              Your entire New York City rental journey in one place
            </h1>

            <div className={`homepage-css mt-6 rounded-2xl p-4 shadow sm:p-6 sm:shadow-none`}>
              <TabSelector />
              {selectedTab == 'reviews' && <ReadReview hideTitle={true} />}
              {selectedTab == 'apartments' && (
                <RentApartment
                  hideTitle={true}
                  listingsLoad={listingsLoad}
                  listingsDataCount={listingsDataCount}
                />
              )}
            </div>
          </div>
        </div>
        <div className="relative bg-light-10">
          <div className="mx-auto max-w-screen-lg px-4 py-8 lg:px-0">
            <div className="space-y-8">
              {selectedTab === 'apartments' && (
                <>
                  {/* Special Gems Section */}
                  <SpecialGems />

                  {/* Verified Listings Section */}
                  <div className="space-y-4">
                    <Link href={'/nyc/apartments-for-rent-nyc'}>
                      <a
                        className="inline-flex items-center text-xl font-semibold text-dark-900"
                        onClick={() => segment?.explore_verified_listings('Landing')}
                      >
                        Verified Apartment Listings
                        <FiChevronRight className="ml-1" />
                      </a>
                    </Link>
                    <ResponsiveCardGrid
                      mobileCarouselConfig={{
                        afterChange: () => {
                          segment?.verified_listings_scroll()
                        },
                      }}
                    >
                      {initialList.slice(0, 3).map((listing, index) => (
                        <ListingUnitCards
                          key={`listing-${listing.id}-${index}`}
                          unit={listing}
                          segmentBase="home"
                          disableFullScreenImage={true}
                        />
                      ))}
                    </ResponsiveCardGrid>
                  </div>

                  {/* Rent Calculator Section */}
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-dark-900">
                      Calculate legal rent increase for your rent-stabilized apartment
                    </h2>
                    <RentCalculator />
                  </div>
                </>
              )}
              {selectedTab === 'reviews' && <ReviewsTab />}
              {/* All you need to know Section */}
              <div className="space-y-4">
                <Link href={'/blogs'} passHref>
                  <a
                    target="_blank"
                    className="inline-flex items-center text-xl font-semibold text-dark-900"
                    onClick={() => segment?.explore_all_blogs()}
                  >
                    All you need to know
                    <FiChevronRight className="ml-1" />
                  </a>
                </Link>
                <ResponsiveCardGrid
                  mobileCarouselConfig={{
                    afterChange: () => {
                      segment?.explore_resource_scroll()
                    },
                  }}
                  gridClasses="grid grid-cols-1 gap-4 sm:grid-cols-3"
                >
                  {sortedFeaturedResources.slice(0, 3).map((resource) => (
                    <BlogCard key={resource.id} blog={resource} segmentBase="explore" />
                  ))}
                </ResponsiveCardGrid>
              </div>
              {/* Marketplace Section */}
              {selectedTab === 'apartments' && <MarketPlace />}

              {/* FAQs Section */}
              <LandingFaqs />

              {/* Download App Section */}
              <DownloadApp />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export const getStaticProps = wrapper.getStaticProps((store) => async () => {
  const [list] = await Promise.all([
    getNewVerifiedListings(1, null, { isHomepage: 'yes' }),
    store.dispatch(fetchFeaturedResources()),
    store.dispatch(fetchTopRatedBuildings(3, 'New York')),
    store.dispatch(fetchTopRatedContacts(3, 'New York')),
    store.dispatch(fetchTopReviewedBuildings(3, 'New York')),
    store.dispatch(fetchTopReviewedContacts(3, 'New York')),
  ])

  return {
    props: {
      initialList: list.results,
    },
    revalidate: 900, // 15 minutos
  }
})

export default NewLandingPage
